import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import LeftArrowIcon from "@/assets/svgs/left-arrow-icon";
import RightArrowIcon from "@/assets/svgs/right-arrow-icon";
import { Tab } from "@/elements/components/ui/tab";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@/elements/components/utils/convex-api";
import { addDays, format, isPast, startOfDay, subDays } from "date-fns";
import TutorDailySessions from "./tutor-daily-sessions";
import TutorBookings from "./tutor-bookings";
import Loader from "../shared/loader";
import { Button } from "../ui/button";
import { useRouter } from "expo-router";
import CheckCircleInactiveIcon from "@/assets/svgs/check-circle-inactive-icon";
import CheckCircleActiveIcon from "@/assets/svgs/check-circle-active-icon";
import TutorLessonHistoryHome from "../lesson-history/tutor-lesson-history-home";
import ReviewLessonReportModal from "../modals/review-lesson-report-modal";
import TutorStudents from "./tutor-students";

function getUtcDayRange(startDate: Date) {
  const startDateUTC = new Date(
    Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  );
  const endDateUTC = new Date(
    Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      23,
      59,
      59,
      999
    )
  );
  return { startDateUTC, endDateUTC };
}

function TutorHome() {
  const { data: currentUser } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {})
  );
  const {
    data: profileCompletionStatus,
    isLoading: isProfileCompletionStatusLoading,
  } = useQuery(
    convexQuery(api.tutor.home.queries.getProfileCompletionStatus, {})
  );
  const name = currentUser?.name;
  const [startDate, setStartDate] = useState(startOfDay(new Date()));
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(
    null
  );
  const currentMonth = format(startDate, "MMMM");
  const dayKey = startDate.getTime();
  const { startDateUTC, endDateUTC } = useMemo(
    () => getUtcDayRange(startDate),
    [dayKey]
  );
  const { data: subjects } = useQuery(
    convexQuery(api.tutor.home.queries.getSubjects, !!currentUser ? {} : "skip")
  );
  const { data: dailySessions } = useQuery({
    ...convexQuery(api.tutor.home.queries.getSessions, {
      startDate: startDateUTC.getTime(),
      endDate: endDateUTC.getTime(),
    }),
    enabled: !!currentUser,
  });
  const safeSubjects = subjects ?? [];
  const allSessions = dailySessions ?? [];
  const dailySessionsCount = allSessions.length;
  const sessionCountBySubject = useMemo(() => {
    const counts = new Map<string, number>();
    for (const session of allSessions) {
      counts.set(
        session.subjectId,
        (counts.get(session.subjectId) ?? 0) + 1
      );
    }
    return counts;
  }, [allSessions]);
  const sortedSubjects = useMemo(() => {
    return [...safeSubjects].sort((a, b) => {
      const aCount = sessionCountBySubject.get(a.id) ?? 0;
      const bCount = sessionCountBySubject.get(b.id) ?? 0;
      if (aCount > 0 && bCount === 0) return -1;
      if (bCount > 0 && aCount === 0) return 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
  }, [safeSubjects, sessionCountBySubject]);
  const displayedSessions = useMemo(() => {
    if (!selectedSubjectId) return [];
    return allSessions.filter(
      (session) => session.subjectId === selectedSubjectId
    );
  }, [allSessions, selectedSubjectId]);
  const router = useRouter();
  const [
    isReviewLessonReportModalVisible,
    setIsReviewLessonReportModalVisible,
  ] = useState(false);
  const { data: lessonReportToAcknowledge } = useQuery(
    convexQuery(api.tutor.home.queries.getLessonReportToAcknowledge, {})
  );
  const lessonReportToAcknowledgeId = lessonReportToAcknowledge?._id;
  const { mutateAsync: acknowledgeLessonReport } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.home.mutations.acknowledgeLessonReport
    ),
  });

  useEffect(() => {
    setSelectedSubjectId(null);
  }, [dayKey]);

  useEffect(() => {
    if (selectedSubjectId !== null || sortedSubjects.length === 0) return;

    const firstSubjectWithLessons = sortedSubjects.find(
      (subject) => (sessionCountBySubject.get(subject.id) ?? 0) > 0
    );
    setSelectedSubjectId(
      firstSubjectWithLessons?.id ?? sortedSubjects[0]?.id ?? null
    );
  }, [selectedSubjectId, sortedSubjects, sessionCountBySubject]);

  useEffect(() => {
    if (lessonReportToAcknowledgeId) {
      setIsReviewLessonReportModalVisible(true);
    }
  }, [lessonReportToAcknowledgeId]);

  if (isProfileCompletionStatusLoading) {
    return <Loader />;
  }

  const percentage = profileCompletionStatus?.percentage ?? 0;
  const profileCompletedAt = profileCompletionStatus?.profileCompletedAt;
  const steps = profileCompletionStatus?.steps ?? [];

  return (
    <Fragment>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="h-3" />
          <View className="mx-4">
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[32px] text-gray-800">
                Hi {name}
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-gray-800 leading-[1.5]">
                Here&apos;s your teaching plan at a glance
              </Text>
            </View>
            <View className="h-8" />

            {!profileCompletedAt && (
              <Fragment>
                <View className="bg-teachLoop-creamDark rounded-2xl py-6 px-4 gap-2">
                  <View className="flex-row justify-between">
                    <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                      Profile completion
                    </Text>
                    <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                      {percentage}% done
                    </Text>
                  </View>
                  <View className="bg-gray-100 rounded-[5px] h-1">
                    <View
                      className={"bg-teal-500 h-1 rounded-[5px]"}
                      style={{
                        width: `${percentage}%`,
                      }}
                    />
                  </View>
                  <Text className="text-sm text-black leading-[1.5] font-normal font-geologica-regular">
                    Completed profiles are more likely to get tuition requests.
                  </Text>
                  <View className="gap-2">
                    {steps.map((step) => (
                      <View
                        className="flex-row items-center gap-2"
                        key={step.id}
                      >
                        {step.completed ? (
                          <CheckCircleActiveIcon
                            className="size-6"
                            pathClassName="fill-black"
                          />
                        ) : (
                          <CheckCircleInactiveIcon
                            className="size-6"
                            pathClassName="stroke-black"
                          />
                        )}
                        <Text className="text-sm text-black leading-[1.5] font-normal font-geologica-regular">
                          {step.label}
                        </Text>
                      </View>
                    ))}
                  </View>

                  <View className="items-end mt-5">
                    <Button
                      size="sm"
                      onPress={() => {
                        router.push("/(app)/profile-settings");
                      }}
                    >
                      <Button.Text size="sm">Complete profile</Button.Text>
                    </Button>
                  </View>
                </View>
                <View className="h-8" />
              </Fragment>
            )}

            <Fragment>
              <View className="flex-row items-start justify-between">
                <View className="gap-2">
                  <Text className="font-geologica-medium font-medium text-[20px] text-gray-800 leading-[1.5]">
                    Your daily schedule
                  </Text>
                  <Text className="font-geologica-regular font-normal text-[16px] text-gray-800 leading-[1.5]">
                    {format(startDate, "EEEE do")} {currentMonth}
                  </Text>
                </View>
                <View className="gap-2 items-center">
                  <View className="flex-row items-center gap-6 self-end">
                    <Pressable
                      className="disabled:opacity-50"
                      disabled={isPast(startDate)}
                      onPress={() => setStartDate(subDays(startDate, 1))}
                    >
                      <LeftArrowIcon opacity={isPast(startDate) ? 0.5 : 1} />
                    </Pressable>
                    <RightArrowIcon
                      onPress={() => setStartDate(addDays(startDate, 1))}
                    />
                  </View>

                  {dailySessionsCount > 0 && (
                    <View className="px-2 py-1 bg-teachLoop-creamDark rounded-md">
                      <Text className="font-geologica-medium font-medium text-xs text-teachLoop-textPrimary leading-[1.5] uppercase">
                        {dailySessionsCount} lessons left
                      </Text>
                    </View>
                  )}
                </View>
              </View>

              <View className="h-6" />
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerClassName="gap-1"
              >
                {sortedSubjects.map((subject) => (
                  <Tab
                    size="md"
                    key={subject.id}
                    intent={
                      selectedSubjectId === subject.id ? "active" : "inActive"
                    }
                    onPress={() => setSelectedSubjectId(subject.id)}
                  >
                    <Tab.Text size="md">{subject.name}</Tab.Text>
                    {selectedSubjectId === subject.id && <Tab.Dot />}
                  </Tab>
                ))}
              </ScrollView>
              {sortedSubjects.length > 0 && (
                <Fragment>
                  <View className="h-6" />
                  <View className="h-[1px] bg-gray-200" />
                  <View className="h-4" />
                </Fragment>
              )}
              <View className="gap-4">
                <TutorDailySessions
                  sessions={displayedSessions}
                  startDate={startDate}
                />
                <TutorBookings />
              </View>
              <View className="h-6" />

              <TutorLessonHistoryHome />

              <TutorStudents />
            </Fragment>
          </View>
        </ResponsiveContent>
      </ScrollView>

      <ReviewLessonReportModal
        isVisible={isReviewLessonReportModalVisible}
        onClose={() => {
          setIsReviewLessonReportModalVisible(false);
        }}
        onDismiss={async () => {
          setIsReviewLessonReportModalVisible(false);
          if (lessonReportToAcknowledgeId) {
            await acknowledgeLessonReport({
              lessonReportId: lessonReportToAcknowledgeId,
            });
          }
        }}
        onAccept={async () => {
          setIsReviewLessonReportModalVisible(false);
          if (lessonReportToAcknowledgeId) {
            await acknowledgeLessonReport({
              lessonReportId: lessonReportToAcknowledgeId,
            });
            router.push(`/(app)/lesson-history/${lessonReportToAcknowledgeId}`);
          }
        }}
      />
    </Fragment>
  );
}

export default TutorHome;
